import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SnapCarousel } from '@/components/SnapCarousel';
import { TeamLogo } from '@/components/TeamLogo';
import SeriesCard from '@/components/SeriesCard';
import { RevealHighlight } from '@/components/RevealHighlight';

interface RecentResultsCarouselProps {
  seriesList: any[];
  groupId: string;
  userBets: any[];
  onRevealClick?: (seriesId: string) => void;
}

// Round priority — used to find the *latest* round that has completed series.
// During R1 mid-flight the strip shows R1 finishes; once R2 kicks off it stays
// on R1 only until R2 has its own resolved series, then flips forward.
const ROUND_ORDER = [
  'first_round',
  'round1',
  'west_semis',
  'east_semis',
  'west_finals',
  'east_finals',
  'finals',
  'champion',
];

function getMostRecentCompletedRound(seriesList: any[]): string | null {
  const completed = seriesList.filter((s) => s.status === 'completed' && s.round);
  if (completed.length === 0) return null;
  const rounds = Array.from(new Set(completed.map((s) => s.round)));
  rounds.sort((a, b) => {
    const ai = ROUND_ORDER.indexOf(a);
    const bi = ROUND_ORDER.indexOf(b);
    return (bi === -1 ? -1 : bi) - (ai === -1 ? -1 : ai);
  });
  return rounds[0] ?? null;
}

function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isDesktop;
}

export function RecentResultsCarousel({
  seriesList,
  groupId,
  userBets,
  onRevealClick,
}: RecentResultsCarouselProps) {
  const round = getMostRecentCompletedRound(seriesList);
  const recentSeries = round
    ? seriesList.filter((s) => s.status === 'completed' && s.round === round)
    : [];
  const isDesktop = useIsDesktop();

  if (recentSeries.length === 0) return null;

  const getBetForSeries = (seriesId: string) =>
    userBets.find((b) => b.series_id === seriesId);

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight">תוצאות אחרונות</h2>
        <Link
          to={`/group/${groupId}/series`}
          className="text-sm text-accent hover:underline"
        >
          כל הסדרות ←
        </Link>
      </div>

      {isDesktop ? (
        /* Desktop: vertical list, reuse the full SeriesCard */
        <div className="space-y-0">
          {recentSeries.map((series, idx) => (
            <SeriesCard
              key={series.id}
              series={series}
              groupId={groupId}
              userBet={getBetForSeries(series.id)}
              index={idx}
              onRevealClick={onRevealClick}
            />
          ))}
        </div>
      ) : (
        /* Mobile: compact horizontal strip — shows final score + winner + user-bet outcome */
        <SnapCarousel cardWidth={160} cardWidthDesktop={220} gap={8} peekAmount={20}>
          {recentSeries.map((series) => {
            const bet = getBetForSeries(series.id);
            const isExactHit = bet?.is_correct_score === true;
            const isWinnerOnly =
              bet?.is_correct_winner === true && bet?.is_correct_score === false;
            const isMiss =
              bet?.is_correct_score === false && bet?.is_correct_winner === false;

            const finalScore = series.final_score
              ? series.final_score.split('-').reverse().join('-')
              : `${series.away_wins ?? 0}-${series.home_wins ?? 0}`;

            const cardInner = (
              <div className="relative rounded-2xl p-2 h-[130px] flex flex-col items-center justify-center gap-1 overflow-hidden bg-card border border-violet-400/40 shadow-[inset_0_0_14px_rgba(167,139,250,0.12)]">
                {onRevealClick && <RevealHighlight variant="compact" />}
                <div className="flex items-center gap-1 mt-3">
                  <TeamLogo teamName={series.team_home} size={26} />
                  <span className="text-[11px] text-muted-foreground font-bold whitespace-nowrap" dir="ltr">
                    {finalScore}
                  </span>
                  <TeamLogo teamName={series.team_away} size={26} />
                </div>
                {series.winner && (
                  <span className="text-[10px] text-accent font-bold truncate max-w-[140px]">
                    {series.winner} ניצחה
                  </span>
                )}
                <div>
                  {isExactHit ? (
                    <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                      ✅ +{bet?.points_earned || 0} נק׳
                    </span>
                  ) : isWinnerOnly ? (
                    <span className="bg-yellow-500/15 text-yellow-400 text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                      🏆 +{bet?.points_earned || 0} נק׳
                    </span>
                  ) : isMiss ? (
                    <span className="bg-red-500/10 text-red-400 text-[10px] px-2 py-0.5 rounded-full">
                      ❌
                    </span>
                  ) : (
                    <span className="bg-muted/30 text-muted-foreground text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                      לא הימרת
                    </span>
                  )}
                </div>
              </div>
            );

            if (onRevealClick) {
              return (
                <button
                  key={series.id}
                  type="button"
                  onClick={() => onRevealClick(series.id)}
                  className="block w-full"
                >
                  {cardInner}
                </button>
              );
            }
            return (
              <Link
                key={series.id}
                to={`/group/${groupId}/series/${series.id}`}
                className="block"
              >
                {cardInner}
              </Link>
            );
          })}
        </SnapCarousel>
      )}
    </div>
  );
}

export default RecentResultsCarousel;
